import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import { ApplicationError } from 'features/error-handling/ApplicationError';
import { isFirebasePermissionDeniedError } from 'features/error-handling/errorUtils';
import { logger } from 'features/logger/logger';

//eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T is more readable than using unknown (onSnapshot contravariance)
export const listenForFirestoreDocumentSnapshots = async <T extends object>(
  cacheEntryRemoved: Promise<void>,
  documentPath: string,
  onSnapshot: (data: T) => void,
  onError: (error: Error) => void,
  onSnapshotEmpty?: () => void
) => {
  let callbackId: string | undefined;

  try {
    callbackId = await addFirestoreSnapshotListener<T>(
      documentPath,
      onSnapshot,
      onError,
      onSnapshotEmpty
    );
  } catch (error) {
    logger.error(
      `Error while adding document subscription for RTKQ slice cache entry (${documentPath})`
    );
    const appError = new ApplicationError('PHW-F-002');
    appError.cause = error;

    throw appError;
  }

  await cacheEntryRemoved;

  if (callbackId) {
    removeFirestoreSnapshotListener({
      callbackId,
      documentPath,
    });
  } else {
    logger.error(
      `Subscription callbackId not available after RTKQ slice cache entry was removed (${documentPath})`
    );
  }
};

//eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T is more readable than using unknown (onSnapshot contravariance)
export const listenForFirestoreCollectionSnapshots = async <T extends object>(
  cacheEntryRemoved: Promise<void>,
  collectionPath: string,
  onSnapshot: (data: T[]) => void,
  onError: (error: Error) => void
) => {
  let callbackId: string | undefined;

  try {
    callbackId = await addFirestoreCollectionSnapshotListener<T>(
      collectionPath,
      onSnapshot,
      onError
    );
  } catch (error) {
    logger.error(
      `Error while adding collection subscription for RTKQ slice cache entry (${collectionPath})`
    );
    const appError = new ApplicationError('PHW-F-002');
    appError.cause = error;

    throw appError;
  }

  await cacheEntryRemoved;

  if (callbackId) {
    removeFirestoreSnapshotListener({
      callbackId,
      documentPath: collectionPath,
    });
  } else {
    logger.error(
      `Subscription callbackId not available after RTKQ slice cache entry was removed (${collectionPath})`
    );
  }
};

//eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T is more readable than using unknown (onSnapshot contravariance)
const addFirestoreSnapshotListener = async <T extends object>(
  documentPath: string,
  onSnapshot: (data: T) => void,
  onError: (error: Error) => void,
  onSnapshotEmpty?: () => void
) => {
  const callbackId = await FirebaseFirestore.addDocumentSnapshotListener(
    {
      reference: documentPath,
    },
    (event, error) => {
      try {
        logger.debug(`Snapshot listener invoked (${documentPath})`);

        if (error) {
          if (isFirebasePermissionDeniedError(error)) {
            const appError = new ApplicationError('PHW-F-001');
            appError.cause = error;
            throw appError;
          }
          throw error;
        }

        if (!event) {
          throw new Error(
            `Event is not available in snapshot listener callback (${documentPath})`
          );
        }

        if (event.snapshot.data) {
          const data = event.snapshot.data as T;
          logger.debug(`Received data in snapshot (${documentPath})`, {
            details: data,
          });

          onSnapshot(data);
        } else {
          // It can happen that snapshot listener is invoked twice, first time with data = null
          // (for example when participation document is created by initializePreScreening onCall function)
          onSnapshotEmpty?.();
          logger.debug(`Snapshot does not contain data (${documentPath})`);
        }
      } catch (error) {
        onError(error as Error);
      }
    }
  );

  logger.info(`Subscribed to document updates (${documentPath})`);

  return callbackId;
};

//eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T is more readable than using unknown (onSnapshot contravariance)
const addFirestoreCollectionSnapshotListener = async <T extends object>(
  collectionPath: string,
  onSnapshot: (data: T[]) => void,
  onError: (error: Error) => void
) => {
  const callbackId = await FirebaseFirestore.addCollectionSnapshotListener(
    {
      reference: collectionPath,
    },
    (event, error) => {
      try {
        logger.debug(
          `Collection snapshot listener invoked (${collectionPath})`
        );

        if (error) {
          if (isFirebasePermissionDeniedError(error)) {
            const appError = new ApplicationError('PHW-F-001');
            appError.cause = error;
            throw appError;
          }
          throw error;
        }

        if (!event) {
          throw new Error(
            `Event is not available in collection snapshot listener callback (${collectionPath})`
          );
        }

        // Drop tombstone snapshots (data === null) so consumers never receive a
        // null document in the collection.
        const data = event.snapshots
          .map(snapshot => snapshot.data as T | null)
          .filter((document): document is T => document !== null);

        onSnapshot(data);
      } catch (error) {
        onError(error as Error);
      }
    }
  );

  logger.info(`Subscribed to collection updates (${collectionPath})`);

  return callbackId;
};

const removeFirestoreSnapshotListener = (params: {
  callbackId: string;
  documentPath: string;
}) => {
  void FirebaseFirestore.removeSnapshotListener({
    callbackId: params.callbackId,
  });

  logger.info(`Unsubscribed from document updates (${params.documentPath})`, {
    details: { callbackId: params.callbackId },
  });
};
